import { useEffect, useRef, useState } from "react";
import BrandMark from "../components/BrandMark";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "./landing.css";

const MARQUEE = [
  "Order-flow footprint",
  "Market profile",
  "100+ indicators",
  "Python strategies",
  "Backtesting",
  "Bar-by-bar replay",
  "Drawing tools",
  "Multi-chart layouts",
];

const ABOUT =
  "Lightweight Terminal is a charting platform built by traders. Markets move fast and most tools slow you down. We built one screen to see the move early, test your idea in Python, and measure every trade you would take.";

// Illustrative read-outs only (labelled as such on the page).
const SIGNALS = [
  ["FX", "EURUSD 15m", "Stacked buy imbalance · 3 levels", "pos", "BULLISH"],
  ["IDX", "US100 5m", "Delta divergence at the high", "neg", "BEARISH"],
  ["FX", "GBPUSD 1h", "Absorption at the session low", "wat", "WATCH"],
  ["CRY", "BTCUSD 1h", "Value area migrating up", "pos", "BULLISH"],
  ["FX", "USDJPY 15m", "Unfinished auction above", "wat", "WATCH"],
  ["IDX", "US30 5m", "Sell imbalance at resistance", "neg", "BEARISH"],
];

function prefersReducedMotion() {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** All the canvas / scroll effects from the design. Returns a cleanup function. */
function runEffects(root) {
  const q = (s) => root.querySelector(s);
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  const reduce = prefersReducedMotion();
  const cleanups = [];
  const fit = (c) => {
    c.width = c.offsetWidth * DPR;
    c.height = c.offsetHeight * DPR;
  };

  // galaxy (hero + contact)
  const galaxy = (sel, cxF, cyF, n) => {
    const c = q(sel);
    const x = c && c.getContext && c.getContext("2d");
    if (!x) return;
    let st = [];
    let raf = 0;
    const rs = () => {
      fit(c);
      st = Array.from({ length: n }, () => ({
        a: Math.random() * 6.28,
        r: Math.pow(Math.random(), 1.8) * Math.max(c.width, c.height) * 0.42,
        s: Math.random() * 0.0007 + 0.0002,
        z: Math.random() * 1.5 + 0.3,
      }));
    };
    rs();
    window.addEventListener("resize", rs);
    const frame = () => {
      const W = c.width, H = c.height, cx = W * cxF, cy = H * cyF;
      x.fillStyle = "rgba(5,6,8,.3)";
      x.fillRect(0, 0, W, H);
      const g = x.createRadialGradient(cx, cy, 0, cx, cy, W * 0.3);
      g.addColorStop(0, "rgba(111,143,232,.09)");
      g.addColorStop(1, "rgba(111,143,232,0)");
      x.fillStyle = g;
      x.fillRect(0, 0, W, H);
      for (const p of st) {
        p.a += p.s;
        x.fillStyle = `rgba(220,228,255,${0.2 + p.z * 0.4})`;
        x.fillRect(cx + Math.cos(p.a) * p.r, cy + Math.sin(p.a) * p.r * 0.5, p.z * DPR, p.z * DPR);
      }
      if (!reduce) raf = requestAnimationFrame(frame);
    };
    frame();
    cleanups.push(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", rs);
    });
  };
  galaxy("#stars", 0.72, 0.42, 450);
  galaxy("#stars2", 0.55, 0.5, 300);

  // about: words light up while scrolling
  const at = q("#aboutText");
  if (at) {
    at.innerHTML = at.textContent.split(" ").map((w) => `<span>${w}</span>`).join(" ");
    const words = [...at.children];
    const light = () => {
      const r = at.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (window.innerHeight * 0.85 - r.top) / (r.height + window.innerHeight * 0.35)));
      const k = Math.floor(p * words.length);
      words.forEach((w, i) => w.classList.toggle("on", i < k));
    };
    window.addEventListener("scroll", light, { passive: true });
    light();
    cleanups.push(() => window.removeEventListener("scroll", light));
  }

  // liquidity heatmap (simulated)
  const hc = q("#heat");
  const hx = hc && hc.getContext && hc.getContext("2d");
  if (hx) {
    const COLS = 160, ROWS = 70, BOOKW = 0.12;
    let grid = [], path = [], trades = [], mid = ROWS / 2, walls = [];
    const newWall = () => ({ row: Math.floor(Math.random() * ROWS), size: Math.random() * 0.7 + 0.3, life: Math.random() * 120 + 40 });
    walls = Array.from({ length: 7 }, newWall);
    const column = () => {
      const col = new Float32Array(ROWS);
      for (let r = 0; r < ROWS; r++) col[r] = Math.random() * 0.12 * Math.exp(-Math.abs(r - mid) / 30);
      walls.forEach((w) => {
        if (w.life-- > 0) {
          for (let d = -1; d <= 1; d++) {
            const rr = w.row + d;
            if (rr >= 0 && rr < ROWS) col[rr] += w.size * (d ? 0.45 : 1);
          }
        } else Object.assign(w, newWall());
      });
      return col;
    };
    const step = () => {
      mid += (Math.random() - 0.5) * 1.4 + (ROWS / 2 - mid) * 0.01;
      grid.push(column());
      path.push(mid);
      trades.push(Math.random() < 0.18 ? { side: Math.random() < 0.5 ? 1 : -1, s: Math.random() * 4 + 2 } : null);
      if (grid.length > COLS) { grid.shift(); path.shift(); trades.shift(); }
    };
    for (let i = 0; i < COLS; i++) step();
    const heat = (v) => {
      v = Math.min(1, v);
      return `rgb(${Math.round(15 + v * 200)},${Math.round(25 + v * 215)},${Math.round(60 + v * 195)})`;
    };
    const draw = () => {
      fit(hc);
      const W = hc.width, H = hc.height, cw = (W * (1 - BOOKW)) / COLS, rh = H / ROWS;
      hx.fillStyle = "#05070c";
      hx.fillRect(0, 0, W, H);
      grid.forEach((col, i) => {
        for (let r = 0; r < ROWS; r++) {
          if (col[r] < 0.04) continue;
          hx.fillStyle = heat(col[r]);
          hx.globalAlpha = Math.min(1, col[r] * 1.3);
          hx.fillRect(i * cw, r * rh, cw + 1, rh + 0.5);
        }
      });
      hx.globalAlpha = 1;
      hx.strokeStyle = "#e5e7eb";
      hx.lineWidth = 1.6 * DPR;
      hx.beginPath();
      path.forEach((p, i) => {
        const X = i * cw + cw / 2, Y = p * rh;
        if (i) hx.lineTo(X, Y); else hx.moveTo(X, Y);
      });
      hx.stroke();
      trades.forEach((t, i) => {
        if (!t) return;
        hx.strokeStyle = t.side > 0 ? "#22c55e" : "#ef4444";
        hx.lineWidth = 1.3 * DPR;
        hx.beginPath();
        hx.arc(i * cw + cw / 2, path[i] * rh, t.s * DPR, 0, 6.28);
        hx.stroke();
      });
      const last = grid[grid.length - 1], bx = W * (1 - BOOKW), lastY = path[path.length - 1] * rh;
      hx.fillStyle = "#07090d";
      hx.fillRect(bx, 0, W - bx, H);
      hx.strokeStyle = "rgba(255,255,255,.08)";
      hx.beginPath(); hx.moveTo(bx, 0); hx.lineTo(bx, H); hx.stroke();
      for (let r = 0; r < ROWS; r++) {
        const y = r * rh, len = Math.min(1, last[r] + 0.05 + Math.random() * 0.08) * (W - bx - 10 * DPR);
        hx.fillStyle = y < lastY ? "rgba(239,68,68,.55)" : "rgba(34,197,94,.55)";
        hx.fillRect(bx + 4 * DPR, y + 1, len, rh - 2);
      }
      hx.setLineDash([3 * DPR, 3 * DPR]);
      hx.strokeStyle = "rgba(255,255,255,.35)";
      hx.beginPath(); hx.moveTo(0, lastY); hx.lineTo(W, lastY); hx.stroke();
      hx.setLineDash([]);
      hx.font = `${10 * DPR}px Geist Mono, monospace`;
      const tw = 34 * DPR;
      hx.fillStyle = "#e5e7eb";
      hx.fillRect(bx - tw - 6 * DPR, lastY - 8 * DPR, tw, 16 * DPR);
      hx.fillStyle = "#05060a";
      hx.fillText("LAST", bx - tw - 1 * DPR, lastY + 4 * DPR);
    };
    let visible = true;
    const vis = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver((e) => { visible = e[0].isIntersecting; }) : null;
    if (vis) vis.observe(hc);
    const id = setInterval(() => { if (visible) { step(); draw(); } }, reduce ? 2000 : 140);
    draw();
    window.addEventListener("resize", draw);
    cleanups.push(() => { clearInterval(id); window.removeEventListener("resize", draw); if (vis) vis.disconnect(); });
  }

  // signal feed (illustrative)
  const fd = q("#feed");
  if (fd) {
    let si = 0;
    const push = () => {
      const s = SIGNALS[si++ % SIGNALS.length];
      const d = document.createElement("div");
      d.className = "item";
      d.innerHTML = `<span class="mk">${s[0]}</span><div><b>${s[1]}</b><small>${s[2]}</small></div><span class="pill ${s[3]}">${s[4]}</span>`;
      fd.prepend(d);
      while (fd.children.length > 4) fd.lastChild.remove();
    };
    for (let i = 0; i < 4; i++) push();
    const id = setInterval(push, 3200);
    cleanups.push(() => clearInterval(id));
  }

  // tech visuals
  const sp = q("#spark");
  const sx = sp && sp.getContext && sp.getContext("2d");
  if (sx) {
    const lines = [[], [], []];
    for (let k = 0; k < 3; k++) {
      let v = 45;
      for (let i = 0; i < 120; i++) { v = Math.max(10, Math.min(80, v + (Math.random() - 0.5) * 6)); lines[k].push(v); }
    }
    const drawSpark = () => {
      fit(sp);
      const W = sp.width, H = sp.height;
      sx.clearRect(0, 0, W, H);
      ["#6f8fe8", "#22c55e", "rgba(255,255,255,.4)"].forEach((c, k) => {
        const L = lines[k];
        L.shift();
        L.push(Math.max(10, Math.min(80, L[L.length - 1] + (Math.random() - 0.5) * 6)));
        sx.strokeStyle = c;
        sx.lineWidth = 1.2 * DPR;
        sx.beginPath();
        L.forEach((y, i) => { const X = (i / (L.length - 1)) * W, Y = (y / 90) * H; if (i) sx.lineTo(X, Y); else sx.moveTo(X, Y); });
        sx.stroke();
      });
    };
    const id = setInterval(drawSpark, 120);
    drawSpark();
    cleanups.push(() => clearInterval(id));
  }
  const sw = q("#sweep");
  if (sw && !reduce) {
    let ang = 0;
    const id = setInterval(() => { ang = (ang + 3) % 360; sw.setAttribute("transform", `rotate(${ang} 60 60)`); }, 40);
    cleanups.push(() => clearInterval(id));
  }

  // reveal on scroll
  const els = root.querySelectorAll(".rv");
  if (typeof IntersectionObserver === "undefined") {
    els.forEach((el) => el.classList.add("in"));
  } else {
    const io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("in");
          e.target.querySelectorAll("[data-w]").forEach((i) => { i.style.width = i.dataset.w + "%"; });
          io.unobserve(e.target);
        }),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());
  }

  return () => cleanups.forEach((f) => f());
}

export default function LandingPage() {
  const { user, startGuestSession } = useAuth();
  const navigate = useNavigate();
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestError, setGuestError] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = "Lightweight Terminal — Markets, rendered.";
    if (!rootRef.current) return undefined;
    return runEffects(rootRef.current);
  }, []);

  const jump = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const launch = user ? "/app" : "/login";
  const access = user ? "/app" : "/signup";
  const beginGuestTrial = async () => {
    setGuestError("");
    setGuestBusy(true);
    try {
      await startGuestSession();
      navigate("/app", { replace: true });
    } catch (error) {
      setGuestError(error.message || "Could not start one-hour guest access.");
    } finally {
      setGuestBusy(false);
    }
  };
  const marquee = MARQUEE.map((k) => `${k}`);

  return (
    <div className="lp" ref={rootRef}>
      <nav>
        <div className="wrap">
          <a href="#top" className="logo" onClick={jump("top")} aria-label="Lightweight Terminal home">
            <BrandMark />
          </a>
          <div className="links">
            <a href="#features" onClick={jump("features")}>Features</a>
            <a href="#product" onClick={jump("product")}>Product</a>
            <a href="#tech" onClick={jump("tech")}>Technology</a>
          </div>
          <Link to={launch} className="btn">{user ? "Open terminal →" : "Launch app →"}</Link>
        </div>
      </nav>

      <header className="hero" id="top">
        <canvas id="stars" />
        <div className="wrap hero-in">
          <div className="meta"><span className="label">Trading terminal</span><span className="label">/</span><span className="label">Live data · Order flow · Python</span></div>
          <h1 className="rv">Markets,<br /><span className="grad">rendered.</span></h1>
          <div className="hero-row">
            <p className="lead rv">A fast, lightweight terminal for live charts, order flow, Python strategies and backtesting. Everything on one screen, one scroll.</p>
            <div className="cta rv">
              <a href="#features" className="btn solid" onClick={jump("features")}>What it does</a>
              {user ? <Link to={access} className="btn">Open terminal</Link> : <>
                <button type="button" className="btn solid" disabled={guestBusy} onClick={beginGuestTrial}>{guestBusy ? "Starting trial…" : "Use terminal for 1 hour"}</button>
                <Link to={access} className="btn">Create account</Link>
              </>}
            </div>
          </div>
          {guestError && <div role="alert" style={{ position: "relative", color: "#ff9ca7", marginTop: 12, fontSize: 13 }}>{guestError}</div>}
        </div>
        <div className="tabs">
          <a href="#features" onClick={jump("features")}><small>01</small>Pro charting</a>
          <a href="#features" onClick={jump("features")}><small>02</small>Order flow</a>
          <a href="#features" onClick={jump("features")}><small>03</small>Python &amp; backtest</a>
          <span className="sc">SCROLL ↓</span>
        </div>
        <div className="marquee"><div>{[...marquee, ...marquee].map((k, i) => (<span key={i}>{k} <i>◆</i></span>))}</div></div>
      </header>

      <section className="about"><div className="wrap">
        <div className="label">About</div>
        <p id="aboutText">{ABOUT}</p>
      </div></section>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="head">
            <div><div className="label">Live view</div><h2 className="rv">Read the book<br /><span className="ghost">before it moves.</span></h2></div>
            <p className="lead rv">A simulated view of what order flow shows you: resting liquidity at every price level, the traded price, and the aggressive orders that push it.</p>
          </div>
        </div>
        <div className="heat"><canvas id="heat" /></div>
        <div className="wrap legend">
          <div>
            <span style={{ "--c": "linear-gradient(90deg,#1e3a8a,#93c5fd)" }}>Resting liquidity</span>
            <span style={{ "--c": "#e5e7eb", "--r": "2px" }}>Last price</span>
            <span style={{ "--c": "var(--up)", "--r": "50%" }}>Aggressive buy</span>
            <span style={{ "--c": "var(--down)", "--r": "50%" }}>Aggressive sell</span>
          </div>
          <div>Simulated data</div>
        </div>
      </section>

      <section id="features"><div className="wrap">
        <div className="head"><div><div className="label">01 — Features</div><h2 className="rv">Three tools.<br /><span className="ghost">One terminal.</span></h2></div></div>
        <div className="biz">
          <div className="card rv"><div className="n">01</div><h3>Pro charting</h3><p>Live candles on multiple timeframes, up to 8 charts at once, 100+ indicators, drawing tools and bar-by-bar replay, rendered smoothly in the browser.</p><div className="tags"><span>CANDLES</span><span>INDICATORS</span><span>REPLAY</span></div></div>
          <div className="card rv"><div className="n">02</div><h3>Order flow</h3><p>Footprint, volume profile, market profile and delta, built from real broker ticks so you can see who is pushing the price.</p><div className="tags"><span>FOOTPRINT</span><span>PROFILE</span><span>DELTA</span></div></div>
          <div className="card rv"><div className="n">03</div><h3>Python &amp; backtest</h3><p>Write indicators and strategies in Python, run them on your candles, then backtest with stop loss, take profit and costs.</p><div className="tags"><span>PYTHON</span><span>BACKTEST</span><span>METRICS</span></div></div>
        </div>
      </div></section>

      <section id="product" style={{ paddingTop: 0 }}><div className="wrap">
        <div className="head">
          <div><div className="label">02 — Product</div><h2 className="rv">Built for our desk.<br /><span className="ghost">Shipped to traders.</span></h2></div>
          <p className="lead rv">The order-flow reads we look for every day, explained right on the chart.</p>
        </div>
        <div className="prod">
          <div className="card rv">
            <div className="n">ORDER-FLOW READ-OUTS</div>
            <h3>Signals that explain themselves.</h3>
            <p>Stacked imbalances, absorption and delta divergence are marked on the candles, each with the reason it appeared, so you can judge it yourself.</p>
            <div className="specs"><div><span className="label">Platforms</span><b>Any modern browser</b></div><div><span className="label">Data</span><b>Live broker ticks</b></div></div>
            <Link to={access} className="btn">{user ? "Open terminal ↗" : "Try it free ↗"}</Link>
          </div>
          <div className="feed rv"><div className="feed-top"><span>terminal / order flow</span><span><span style={{ color: "var(--up)" }}>●</span> EXAMPLE</span></div><div id="feed" /><div className="feed-top" style={{ border: 0, borderTop: "1px solid var(--line)" }}><span>Illustrative screen · example data</span></div></div>
        </div>
      </div></section>

      <section id="tech" style={{ paddingTop: 0 }}><div className="wrap">
        <div className="head">
          <div><div className="label">03 — Technology</div><h2 className="rv">One stack,<br /><span className="ghost">built in-house.</span></h2></div>
          <p className="lead rv">Data, charts, order flow and backtests run on the same candles, so what you test is what you see.</p>
        </div>
        <div className="bento">
          <div className="card rv"><div className="n">A</div><h3>Market data</h3><p>Ticks and candles from the broker feed, cleaned into one consistent stream.</p><canvas id="spark" height="90" /></div>
          <div className="card rv"><div className="n">B</div><h3>Order flow</h3><p>Imbalances and absorption flagged as the bars form.</p>
            <svg viewBox="0 0 120 120" style={{ width: 110, marginLeft: "auto" }} aria-hidden="true"><g fill="none" stroke="rgba(255,255,255,.12)"><circle cx="60" cy="60" r="55" /><circle cx="60" cy="60" r="38" /><circle cx="60" cy="60" r="20" /></g><path id="sweep" d="M60 60 L60 5 A55 55 0 0 1 99 21 Z" fill="rgba(111,143,232,.35)" /><circle cx="82" cy="38" r="3" fill="#6f8fe8" /></svg></div>
          <div className="row2">
            <div className="card rv"><div className="n">C</div><h3>Python</h3><p>Your own indicators and strategies, run right in the browser.</p>
              <svg viewBox="0 0 160 70" style={{ width: 160, marginLeft: "auto" }} aria-hidden="true"><g stroke="rgba(255,255,255,.35)" fill="none"><path d="M10 35 C60 35 80 10 140 10" /><path d="M10 35 L140 35" /><path d="M10 35 C60 35 80 60 140 60" /><circle cx="10" cy="35" r="4" /><circle cx="140" cy="10" r="4" /><circle cx="140" cy="35" r="4" /><circle cx="140" cy="60" r="4" /></g></svg></div>
            <div className="card rv" id="risk"><div className="n">D</div><h3>Backtest</h3><p>Win rate, profit factor and drawdown for every strategy, side by side with buy and hold. Example figures shown.</p>
              <div className="bar">WIN RATE<div><i data-w="62" /></div></div><div className="bar">PROFIT FACTOR<div><i data-w="74" /></div></div><div className="bar">DRAWDOWN<div><i data-w="33" /></div></div></div>
          </div>
        </div>
        <div className="principles">
          <p><b>— Risk first.</b> Test with stops and costs before anything else.</p>
          <p><b>— Measure everything.</b> Decisions come from data you can verify.</p>
          <p><b>— Own the stack.</b> We build what we depend on.</p>
        </div>
      </div></section>

      <section id="contact" className="contact"><canvas id="stars2" /><div className="wrap">
        <div className="label">04 — Get started</div>
        <h2>Let’s trade<br /><span className="ghost">smarter.</span></h2>
        <Link className="mail" to={access}>{user ? "Open the terminal" : "Create your free account"} <span>↗</span></Link>
        <div className="clinks label">
          {user ? <Link to="/profile">Profile</Link> : (<><Link to="/signup">Sign up</Link><Link to="/login">Sign in</Link></>)}
        </div>
      </div></section>

      <footer><div className="wrap"><span>© 2026 LIGHTWEIGHT TERMINAL</span><span>TRADING INVOLVES RISK · NOT INVESTMENT ADVICE</span></div></footer>
    </div>
  );
}
